// Matched volume display policy.
//
// The API and socket updates ship facts, never display decisions:
//   - matchedVolume: the game's own matched volume
//   - rootGameMatchedVolumeIncludingChildren: the aggregate across the game's
//     whole family (root game + live/half/prop children)
//
// The displayed value is derived:
//   - prop games show their own volume (each prop is its own market)
//   - everything else (roots, live/half children) shows the family aggregate,
//     so a live game keeps showing the volume built up during prematch

export function isPropGame(game) {
  return Boolean(game?.league?.includes('PROPS'));
}

export function displayedMatchedVolume(game) {
  if (!game) return 0;
  if (isPropGame(game)) return game.matchedVolume ?? 0;
  return game.rootGameMatchedVolumeIncludingChildren ?? game.matchedVolume ?? 0;
}

// A matchedVolumeUpdate socket message is a complete, never-null snapshot of
// one game's matched volume: its own volume, the family it belongs to
// (rootGameID), and the family aggregate. One match produces an update for the
// matched game and, when that game is a child, a second update for its root.
export function matchedVolumeFactsFromUpdate(update) {
  return {
    gameID: update.gameID,
    rootGameID: update.rootGameID ?? update.gameID,
    matchedVolume: update.matchedVolume ?? null,
    rootGameMatchedVolumeIncludingChildren:
      update.rootGameMatchedVolumeIncludingChildren ?? null,
  };
}

// One match updates the whole family: the matched game's own volume, and the
// aggregate on every family member (root + siblings) so their derived
// displayed volume stays current.
export function applyMatchedVolumeFacts(game, facts) {
  if (game.id === facts.gameID && facts.matchedVolume != null) {
    game.matchedVolume = facts.matchedVolume;
  }
  if (
    isInGameFamily(game, facts.rootGameID) &&
    facts.rootGameMatchedVolumeIncludingChildren != null
  ) {
    game.rootGameMatchedVolumeIncludingChildren =
      facts.rootGameMatchedVolumeIncludingChildren;
  }
}

function isInGameFamily(game, rootGameID) {
  return game.id === rootGameID || game.rootGameID === rootGameID;
}
